import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toPng } from "html-to-image";
import {
  FaKeyboard,
  FaQrcode,
  FaDownload,
  FaPrint,
  FaLayerGroup,
  FaCut,
  FaTools,
} from "react-icons/fa";

const QRCodeGenerator = () => {
  const [tableCount, setTableCount] = useState("");
  const [qrCodes, setQrCodes] = useState([]);
  const [restaurantId, setRestaurantId] = useState("");

  const qrPerPage = 6; // Limit to 6 QR codes per page

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user?.restaurantId) {
      console.error("Restaurant ID not found in local storage");
      return;
    }
    setRestaurantId(user.restaurantId);
  }, []);

  const handleTableCountChange = (e) => {
    setTableCount(e.target.value);
  };

  const generateQRCodes = () => {
    const codes = [];
    for (let i = 1; i <= tableCount; i++) {
      codes.push(i);
    }
    setQrCodes(codes);
  };

  const paginateQRCodes = () => {
    const pages = [];
    for (let i = 0; i < qrCodes.length; i += qrPerPage) {
      pages.push(qrCodes.slice(i, i + qrPerPage));
    }
    return pages;
  };

  const downloadA4Pages = () => {
    const qrPages = paginateQRCodes();
    qrPages.forEach((page, index) => {
      const qrGrid = document.getElementById(`qr-code-grid-page-${index}`);
      toPng(qrGrid)
        .then((dataUrl) => {
          const link = document.createElement("a");
          link.href = dataUrl;
          link.download = `qr-codes-page-${index + 1}.png`;
          link.click();
        })
        .catch((err) => {
          console.error("Failed to generate image", err);
        });
    });
  };

  return (
    <div className="p-4 min-h-[800px]   ">
      <h1 className="text-2xl font-bold mb-4">
        Generate QR Codes for Your Restaurant
      </h1>

      <div className="mb-4">
        <label className="block text-lg mb-2">
          Enter the number of tables:
        </label>
        <input
          type="number"
          value={tableCount}
          onChange={handleTableCountChange}
          className="  p-2   w-1/3  bg-white text-black border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Number of Tables"
        />
        <button
          onClick={generateQRCodes}
          className="bg-blue-500 text-white rounded p-2 ml-4"
        >
          Generate QR Codes
        </button>
        {qrCodes.length > 0 && (
          <button
            onClick={downloadA4Pages}
            className="mt-4 ml-4 bg-green-500 text-white rounded p-2"
          >
            Download as A4 Pages
          </button>
        )}
      </div>

      <div className="p-4 mt-6 bg-gray-100 rounded-lg">
        <h2 className="text-xl font-bold mb-4">
          How to Generate and Use Your QR Codes
        </h2>

        <div className="space-y-4 text-lg">
          <div className="flex items-start space-x-3">
            <FaKeyboard className="text-blue-500 text-2xl" />
            <p>1. Type in the number of tables in your restaurant.</p>
          </div>

          <div className="flex items-start space-x-3">
            <FaQrcode className="text-green-500 text-2xl" />
            <p>
              2. Click on the <strong>"Generate QR Codes"</strong> button to
              create unique QR codes for each table.
            </p>
          </div>

          <div className="flex items-start space-x-3">
            <FaDownload className="text-purple-500 text-2xl" />
            <p>
              3. Download the QR codes as an <strong>A4 page</strong>. The QR
              codes will be auto-shrunk to fit the A4 size.
            </p>
          </div>

          <div className="flex items-start space-x-3">
            <FaPrint className="text-orange-500 text-2xl" />
            <p>
              4. Print the A4 pages. Make sure the QR codes are clearly fit on the page with borders.
            </p>
          </div>

          <div className="flex items-start space-x-3">
            <FaLayerGroup className="text-yellow-500 text-2xl" />
            <p>
              5. Laminate the printed page to protect the QR codes from, water, wear and
              tear.
            </p>
          </div>

          <div className="flex items-start space-x-3">
            <FaCut className="text-red-500 text-2xl" />
            <p>6. Cut out the individual QR codes from the laminated sheet.</p>
          </div>

          <div className="flex items-start space-x-3">
            <FaTools className="text-teal-500 text-2xl" />
            <p>
              7. Use glue or double-sided tape to stick the QR codes on the
              respective tables.
            </p>
          </div>
        </div>
      </div>
      
      {paginateQRCodes().map((page, pageIndex) => (
        <div
          key={pageIndex}
          id={`qr-code-grid-page-${pageIndex}`}
          className="grid grid-cols-2 gap-6 mt-3 mb-10"
          style={{ width: "793px", pageBreakAfter: "always" }} // Ensures A4 page layout with 6 QR codes
        >
          {page.map((tableNumber) => (
            <div
              key={tableNumber}
              style={{
                width: "380px",
                height: "380px",
                borderColor: "lightcoral",
              }}
              className="text-center p-4 bg-white rounded-lg shadow-lg border-4"
            >
              <h2
                className="text-3xl mb-2 font-bold"
                style={{ color: "lightcoral" }}
              >
                Order food here
              </h2>{" "}
              {/* Light red color */}
              <div
                id={`qr-code-${tableNumber}`}
                className="p-4 flex justify-center items-center"
              >
                <QRCodeCanvas
                  value={`https://www.foodorderoo.com/order/${restaurantId}/${tableNumber}`}
                  size={180}
                  level={"H"}
                  includeMargin={false}
                  fgColor="#000000" // Black QR code
                  bgColor="#FFFFFF" // White background
                />
              </div>
              {/* New line to add the "Order good on the table" text */}
              <p className="text-lg mb-2" style={{ color: "gray" }}>
                FoodOrderoo.com
              </p>
              <h2
                className="text-3xl mb-2 font-bold"
                style={{ color: "lightcoral" }}
              >
                Table {tableNumber}
              </h2>
            </div>
          ))}
        </div>
      ))} 
    </div>
  );
};

export default QRCodeGenerator;
